import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loading } from "@/components/common/Loading";

/**
 * `/groups/invite/{token}` — the ONLY place an invitation token appears in a URL
 * (GRP-DEC-283). The token is never put in a `/g/{handle}` URL. An invalid, expired,
 * disabled, exhausted or unknown token renders the same generic "not available" state.
 */
export default function GroupInviteRedeem() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [accepted, setAccepted] = React.useState(false);
  const [answers, setAnswers] = React.useState({});

  const { data, isLoading, isError } = useQuery({
    queryKey: ["group-invite-token", token],
    queryFn: async () => (await api.get(`/group-invites/link/${token}`)).data,
    retry: false,
  });

  const redeem = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/group-invites/link/${token}/redeem`, {
        rules_accepted: accepted,
        answers: (data?.questions || []).map((q) => ({
          question_id: q.id, value: answers[q.id] ?? null,
        })),
      });
      if (res.state === "active") {
        toast.success(`You've joined ${data.group.display_name}.`);
        navigate(data.group.url || "/groups");
      } else {
        toast.success("Your request was sent to the organizers.");
        navigate("/dashboard/groups");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "This invitation isn't available.");
    } finally { setBusy(false); }
  };

  if (isLoading) return <Loading />;
  if (isError || !data?.group) {
    return (
      <Card className="mx-auto mt-10 max-w-lg p-8 text-center" data-testid="invite-invalid">
        <p className="text-base font-semibold">This invitation isn't available</p>
        <p className="mt-1 text-sm text-muted-foreground">
          The link may have expired, reached its limit, or been turned off.
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/groups")}>
          Discover groups
        </Button>
      </Card>
    );
  }

  const g = data.group;
  const grants = data.grants_membership;
  return (
    <Card className="mx-auto mt-10 max-w-lg space-y-5 p-8" data-testid="invite-redeem-page">
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {grants ? "You're invited to join" : "You're invited to request to join"}
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight" data-testid="invite-group-name">
          {g.display_name}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{g.short_description}</p>
        <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Users className="h-3.5 w-3.5" />{g.member_count} members
        </p>
      </div>

      {data.email_bound && data.email_matches === false ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm"
             data-testid="invite-email-mismatch">
          This invitation was sent to a different email address. Sign in with that address
          (and verify it) to accept.
        </div>
      ) : null}

      {(data.questions || []).map((q) => (
        <div key={q.id} className="space-y-1.5">
          <Label>{q.label}{q.required ? <span className="text-destructive"> *</span> : null}</Label>
          {q.type === "long_text" ? (
            <Textarea rows={3} maxLength={1000} data-testid={`invite-answer-${q.id}`}
                      onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} />
          ) : (
            <Input maxLength={1000} data-testid={`invite-answer-${q.id}`}
                   onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} />
          )}
        </div>
      ))}

      {data.rules_required ? (
        <div className="space-y-2 rounded-lg border p-3">
          <p className="text-sm font-medium">Group rules</p>
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{data.rules}</p>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(Boolean(v))}
                      data-testid="invite-rules-accept" />
            I agree to the group rules
          </label>
        </div>
      ) : null}

      <Button className="w-full" onClick={redeem} disabled={busy}
              data-testid="invite-accept-button">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {grants ? "Join group" : "Send request"}
      </Button>
    </Card>
  );
}
